import React, {useEffect, useState} from "react";
import {createSellAddress, listSellAddresses, listTraders, updateSellAddress} from "../../../api";
import type {SellAddress} from "../../../api";
import type {TraderItem} from "../../../api/admin/directory";

export function SellAddressesSection({merchantId, token}: {merchantId: string; token: string | null}): JSX.Element {
    const [items, setItems] = useState<SellAddress[]>([]);
    const [address, setAddress] = useState("");
    const [ownerTraderId, setOwnerTraderId] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [updating, setUpdating] = useState<string | null>(null);
    const [traders, setTraders] = useState<TraderItem[]>([]);
    const [tradersLoading, setTradersLoading] = useState(true);
    const [tradersError, setTradersError] = useState("");
    const [refresh, setRefresh] = useState(0);
    useEffect(() => {
        let cancelled = false;
        setTraders([]); setOwnerTraderId(""); setTradersError("");
        if (!token) { setTradersLoading(false); return; }
        setTradersLoading(true);
        const load = async (): Promise<void> => {
            try {
                const all: TraderItem[] = [];
                let offset = 0;
                while (!cancelled) {
                    const page = await listTraders(token, {limit: 200, offset});
                    all.push(...page.items);
                    if (!page.hasMore) break;
                    const next = page.nextOffset ?? offset + page.items.length;
                    if (next <= offset) throw new Error("Не удалось загрузить полный список трейдеров");
                    offset = next;
                }
                if (!cancelled) setTraders(all.sort((a, b) => (a.name || a.login).localeCompare(b.name || b.login, "ru")));
            } catch { if (!cancelled) setTradersError("Не удалось загрузить трейдеров. Нажмите «Обновить»."); }
            finally { if (!cancelled) setTradersLoading(false); }
        };
        void load();
        return () => { cancelled = true; };
    }, [token, refresh]);
    const reload = (): void => { if (token) listSellAddresses(token, merchantId).then((r) => setItems(r.items)).catch((e) => setError(String(e))); };
    useEffect(reload, [token, merchantId]);
    const add = async (): Promise<void> => {
        if (!token || !address.trim() || tradersLoading || !traders.some((trader) => trader.id === ownerTraderId)) return;
        setSaving(true); setError("");
        try { await createSellAddress(token, merchantId, {network: "trc20", address: address.trim(), ownerTraderId: ownerTraderId.trim()}); setAddress(""); setOwnerTraderId(""); reload(); }
        catch (e) { setError(String(e)); }
        finally { setSaving(false); }
    };
    const changeActive = async (item: SellAddress): Promise<void> => {
        if (!token || updating) return;
        setUpdating(item.id); setError("");
        try {
            await updateSellAddress(token, merchantId, item.id, !item.active);
            const result = await listSellAddresses(token, merchantId);
            setItems(result.items);
        } catch (e) { setError(String(e)); }
        finally { setUpdating(null); }
    };
    const activeItems = items.filter((item) => item.active);
    const archivedItems = items.filter((item) => !item.active);
    const table = (rows: SellAddress[]): JSX.Element => <div className="table-wrapper"><table><thead><tr><th>Адрес</th><th>Владелец</th><th>Client ref</th><th>Статус</th><th /></tr></thead><tbody>
        {rows.map((item) => <tr key={item.id}><td className="mono">{item.address}</td><td>{item.ownerName || traders.find((trader) => trader.id === item.ownerTraderId)?.name || (tradersLoading ? "Загрузка…" : "Имя недоступно")}</td><td className="mono">{item.clientRef ?? "—"}</td><td>{item.active ? "Активен" : "В архиве"}</td><td><button className="btn btn-ghost btn-sm" type="button" disabled={updating !== null} onClick={() => void changeActive(item)}>{updating === item.id ? "Сохранение…" : item.active ? "В архив" : "Разархивировать"}</button></td></tr>)}
    </tbody></table></div>;
    return <section className="merchant-rate-block merchant-sell-addresses">
        <div className="section-header"><div><h3>Адреса продажи USDT (TRC20)</h3><p className="section-hint">Для клиентов, продающих USDT. Привяжите адрес к трейдеру. Адрес из «Автопополнения» сюда не подходит.</p></div><button className="btn btn-ghost btn-sm" type="button" onClick={() => { reload(); setRefresh((value) => value + 1); }}>Обновить</button></div>
        <div className="merchant-address-form">
            <label className="requisites-field"><span>TRC20 адрес</span><input className="mono" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="T…" autoComplete="off" /></label>
            <label className="requisites-field"><span>Трейдер</span><select value={ownerTraderId} onChange={(e) => setOwnerTraderId(e.target.value)} disabled={saving || tradersLoading || traders.length === 0}>
                <option value="">{tradersLoading ? "Загрузка трейдеров…" : tradersError ? "Список недоступен" : traders.length ? "Выберите трейдера" : "Нет трейдеров"}</option>
                {traders.map((trader) => <option key={trader.id} value={trader.id}>{trader.name || trader.login}{trader.teamName ? ` — ${trader.teamName}` : ""}{trader.login && trader.login !== trader.name ? ` (${trader.login})` : ""}{!trader.isActive ? " — неактивен" : ""}</option>)}
            </select></label>
            <button className="btn btn-primary" type="button" onClick={() => void add()} disabled={saving || tradersLoading || !address.trim() || !ownerTraderId}>Добавить адрес</button>
        </div>
        {tradersError && <div className="details-action-error" role="alert">{tradersError}</div>}
        {error && <div className="details-action-error" role="alert">{error}</div>}
        {activeItems.length ? table(activeItems) : <p className="section-hint">Нет активных адресов.</p>}
        <details className="merchant-address-archive">
            <summary>Архив адресов ({archivedItems.length})</summary>
            <p className="section-hint">Архивные адреса не выдаются для новых заявок. История сохраняется; наблюдение продолжается только по незавершённым заявкам.</p>
            {archivedItems.length ? table(archivedItems) : <p className="section-hint">Архив пуст.</p>}
        </details>
    </section>;
}
